<section
  #authPage
  class="auth-page"
  [class.auth-page--pointer]="isPointerActive()"
  [style.--auth-cursor-x]="cursorX()"
  [style.--auth-cursor-y]="cursorY()"
  (pointermove)="trackPointer($event)"
  (pointerleave)="resetPointer()"
>
  @if (connectionMap(); as map) {
    <svg
      class="auth-connection-layer"
      [attr.viewBox]="'0 0 ' + map.width + ' ' + map.height"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <g class="auth-connection-base">
        @for (path of map.paths; track path.id) {
          <path [attr.d]="path.d" />
        }
      </g>
      <g class="auth-connection-pulses">
        @for (path of map.paths; track path.id) {
          <path
            [attr.class]="'auth-connection-pulse ' + path.pulseClass"
            pathLength="100"
            [attr.d]="path.d"
          />
        }
      </g>
    </svg>
  }

  <main class="auth-form-column">
    <div #formCard class="auth-form-card">
      <div class="auth-form-frame">
        @if (showsBackButton()) {
          <button
            appUiIconButton
            class="auth-back-button"
            variant="ghost"
            size="sm"
            type="button"
            aria-label="Voltar para o login"
            (click)="goBackToLogin()"
          >
            <ng-icon name="heroArrowLeft" size="18" aria-hidden="true" />
          </button>
        }

        <div class="auth-heading">
          <h1>{{ title() }}</h1>
          <p>{{ description() }}</p>
        </div>

        @if (mode() === 'accept-invite') {
          <div class="auth-status">
            @if (acceptInviteStatus() === 'error') {
              <p class="auth-status-error">{{ acceptInviteError() }}</p>
            } @else {
              <p>Só um instante…</p>
            }
          </div>
        } @else {
        <form class="auth-form" novalidate (submit)="submit($event)">
          @if (showsOAuth()) {
            <div class="auth-provider-list" aria-label="Outras formas de acesso">
              @for (option of externalAuthOptions; track option.id) {
                <button
                  appButton
                  variant="secondary"
                  class="auth-provider-button"
                  type="button"
                  [attr.aria-label]="option.ariaLabel"
                  [disabled]="isSubmitting() || isExternalAuthRedirecting()"
                  (click)="startExternalLogin(option.id)"
                >
                  <span
                    class="auth-provider-mark"
                    [class.auth-provider-mark--email]="option.id === 'email-link'"
                    aria-hidden="true"
                  >
                    {{ option.mark }}
                  </span>
                  <span class="auth-provider-label">
                    {{
                      externalAuthRedirecting() === option.id ? option.loadingLabel : option.label
                    }}
                  </span>
                </button>
              }
            </div>

            <div class="auth-divider"><span>ou</span></div>
          }

          @if (mode() === 'signup') {
            <div class="auth-name-grid">
              <label class="auth-field">
                Nome
                <input
                  appInput
                  class="auth-input"
                  type="text"
                  autocomplete="given-name"
                  [value]="firstName()"
                  (input)="firstName.set(readInput($event))"
                />
              </label>

              <label class="auth-field">
                Sobrenome
                <input
                  appInput
                  class="auth-input"
                  type="text"
                  autocomplete="family-name"
                  [value]="lastName()"
                  (input)="lastName.set(readInput($event))"
                />
              </label>
            </div>
          }

          @if (showsEmail()) {
            <label class="auth-field">
              E-mail
              <span class="auth-input-shell">
                <ng-icon name="heroAtSymbol" size="17" aria-hidden="true" />
                <input
                  appInput
                  class="auth-input auth-input--icon"
                  leadingIcon
                  [class.is-invalid]="isEmailInvalid()"
                  [invalid]="isEmailInvalid()"
                  type="email"
                  autocomplete="email"
                  placeholder="Digite seu e-mail"
                  required
                  [value]="email()"
                  (input)="email.set(readInput($event))"
                />
              </span>
            </label>
          }

          @if (showsPassword()) {
            <label class="auth-field">
              Senha
              <span class="auth-input-shell auth-input-shell--password">
                <ng-icon name="heroKey" size="17" aria-hidden="true" />
                <input
                  appInput
                  class="auth-input auth-input--icon"
                  leadingIcon
                  trailingAction
                  [class.is-invalid]="isPasswordInvalid()"
                  [invalid]="isPasswordInvalid()"
                  [type]="isPasswordVisible() ? 'text' : 'password'"
                  [autocomplete]="mode() === 'signup' ? 'new-password' : 'current-password'"
                  placeholder="Digite sua senha"
                  required
                  minlength="8"
                  [value]="password()"
                  (input)="password.set(readInput($event))"
                />
                <button
                  appUiIconButton
                  class="auth-password-toggle"
                  variant="ghost"
                  size="sm"
                  type="button"
                  [attr.aria-label]="isPasswordVisible() ? 'Ocultar senha' : 'Mostrar senha'"
                  [attr.aria-pressed]="isPasswordVisible()"
                  (click)="togglePasswordVisibility()"
                >
                  <ng-icon
                    [name]="isPasswordVisible() ? 'heroEyeSlash' : 'heroEye'"
                    size="17"
                    aria-hidden="true"
                  />
                </button>
              </span>
            </label>
          }

          @if (mode() === 'reset-password') {
            <label class="auth-field">
              Nova senha
              <span class="auth-input-shell auth-input-shell--password">
                <ng-icon name="heroLockClosed" size="17" aria-hidden="true" />
                <input
                  appInput
                  class="auth-input auth-input--icon"
                  leadingIcon
                  trailingAction
                  [class.is-invalid]="isPasswordInvalid()"
                  [invalid]="isPasswordInvalid()"
                  [type]="isPasswordVisible() ? 'text' : 'password'"
                  autocomplete="new-password"
                  placeholder="Digite sua nova senha"
                  required
                  minlength="8"
                  [value]="password()"
                  (input)="password.set(readInput($event))"
                />
                <button
                  appUiIconButton
                  class="auth-password-toggle"
                  variant="ghost"
                  size="sm"
                  type="button"
                  [attr.aria-label]="isPasswordVisible() ? 'Ocultar senha' : 'Mostrar senha'"
                  [attr.aria-pressed]="isPasswordVisible()"
                  (click)="togglePasswordVisibility()"
                >
                  <ng-icon
                    [name]="isPasswordVisible() ? 'heroEyeSlash' : 'heroEye'"
                    size="17"
                    aria-hidden="true"
                  />
                </button>
              </span>
            </label>
          }

          @if (mode() === 'signup') {
            <label class="auth-field">
              Confirmar senha
              <span class="auth-input-shell auth-input-shell--password">
                <ng-icon name="heroLockClosed" size="17" aria-hidden="true" />
                <input
                  appInput
                  class="auth-input auth-input--icon"
                  leadingIcon
                  trailingAction
                  [class.is-invalid]="isConfirmPasswordInvalid()"
                  [invalid]="isConfirmPasswordInvalid()"
                  [type]="isConfirmPasswordVisible() ? 'text' : 'password'"
                  autocomplete="new-password"
                  placeholder="Digite sua senha"
                  required
                  minlength="8"
                  [value]="confirmPassword()"
                  (input)="confirmPassword.set(readInput($event))"
                />
                <button
                  appUiIconButton
                  class="auth-password-toggle"
                  variant="ghost"
                  size="sm"
                  type="button"
                  [attr.aria-label]="
                    isConfirmPasswordVisible()
                      ? 'Ocultar confirmação de senha'
                      : 'Mostrar confirmação de senha'
                  "
                  [attr.aria-pressed]="isConfirmPasswordVisible()"
                  (click)="toggleConfirmPasswordVisibility()"
                >
                  <ng-icon
                    [name]="isConfirmPasswordVisible() ? 'heroEyeSlash' : 'heroEye'"
                    size="17"
                    aria-hidden="true"
                  />
                </button>
              </span>
            </label>
          }

          @if (showsOtp()) {
            <label class="auth-field">
              Código
              <div
                appUiOtpRoot
                [class.is-invalid]="isOtpInvalid()"
                [ngpInputOtpValue]="code()"
                [ngpInputOtpPattern]="'[0-9]'"
                [ngpInputOtpInputMode]="'numeric'"
                [ngpInputOtpDisabled]="isSubmitting()"
                (ngpInputOtpValueChange)="code.set($event)"
              >
                <input
                  appUiOtpInput
                  aria-label="Código de verificação"
                  autocomplete="one-time-code"
                />
                <div appUiOtpSlots>
                  @for (slot of otpSlots; track slot) {
                    <div appUiOtpSlot></div>
                  }
                </div>
              </div>
            </label>
          }

          <button appButton variant="primary" type="submit" [disabled]="isSubmitting()">
            <ng-icon [name]="submitIcon()" size="18" aria-hidden="true" />
            {{ submitLabel() }}
          </button>
        </form>
        }

        <div class="auth-links">
          @if (mode() !== 'login') {
            <a routerLink="/login">Entrar</a>
          }
          @if (mode() !== 'signup') {
            <a routerLink="/signup">Criar conta</a>
          }
          @if (mode() !== 'forgot-password' && mode() !== 'reset-password') {
            <a routerLink="/forgot-password">Esqueci a senha</a>
          }
          @if (mode() === 'email-code') {
            <button appButton variant="ghost" size="sm" type="button" [disabled]="isSubmitting()" (click)="requestNewCode()">
              Reenviar código
            </button>
          }
        </div>
      </div>
    </div>
  </main>

  <aside class="auth-display-column" aria-label="Automato">
    <div class="auth-display-content">
      <a class="auth-display-logo-link" routerLink="/login" aria-label="Automato">
        <img
          [ngSrc]="logoSrc"
          width="210"
          height="33"
          alt="Automato"
          priority
          class="auth-display-logo"
        />
      </a>

      <div class="auth-display-copy">
        <h2>A camada inteligente entre estratégia e execução</h2>
        <p>
          Conecte equipes, agentes e dados em um ecossistema AI-first para operar com clareza e
          gerar resultados.
        </p>
      </div>

      <div class="auth-display-separator" aria-hidden="true">
        <span #connectionMarker class="auth-connection-marker"></span>
      </div>

      <div class="auth-trust" aria-label="Trusted by">
        <p>Trusted by</p>
        <div class="auth-trust-logos">
          <span>RevOps</span>
          <span>Sales</span>
          <span>Success</span>
          <span>Growth</span>
        </div>
      </div>
    </div>
  </aside>
</section>
